/**
 * @file Stay.ai PDP subscription widget — theme “extra sale” integration.
 * Same sale gates and `sale_scope` as `snippets/ia-buy-buttons.liquid`.
 * Reads `data-stayai-*` attributes on the `<purchase-options>` custom element.
 */

/**
 * Struck-through "was" price with screen-reader labels (ADA: Douglass v. Two Lines Inc.).
 * Emits: hidden "Original price: X" + visual <s aria-hidden> + hidden "Sale price: " — the sale figure
 * follows in the caller's markup. Must stay in sync with the Liquid in snippets/stayai-pdp.liquid and
 * the ATC button markup in sections/main-product.liquid, which this script re-renders at runtime.
 *
 * @param {string} formatted  money-formatted original price
 * @returns {string}
 */
function srStrike(formatted) {
  return (
    `<span class="visually-hidden">Original price: ${formatted}</span>` +
    `<s class="fullPrice" aria-hidden="true">${formatted}</s>` +
    `<span class="visually-hidden">Sale price: </span>`
  );
}

/**
 * @param {string|null|undefined} value
 * @param {boolean} [fallback=false]
 * @returns {boolean}
 */
function parseBooleanAttr(value, fallback = false) {
  if (value == null || value === "") return fallback;
  try {
    return JSON.parse(value);
  } catch {
    return value === "true" || value === true;
  }
}

/**
 * Parses theme sale settings from `data-stayai-*` attributes on `<purchase-options>`.
 *
 * @param {Element} el
 * @returns {{ saleEnabled: boolean, inSaleCollection: boolean, saleScope: string, saleDiscountPercent: number, discountFraction: number }}
 */
function parseThemeSaleConfigFromElement(el) {
  const saleEnabled = parseBooleanAttr(el.getAttribute("data-stayai-theme-sale-enabled"), false);
  const inSaleCollection = parseBooleanAttr(el.getAttribute("data-stayai-in-sale-collection"), false);
  const saleScope = (el.getAttribute("data-stayai-sale-scope") || "").trim();
  const discountRaw = el.getAttribute("data-stayai-sale-discount");
  const saleDiscountPercent =
    discountRaw != null && discountRaw !== "" && !Number.isNaN(Number(discountRaw)) ? Number(discountRaw) : 0;
  const discountFraction =
    Number.isFinite(saleDiscountPercent) && saleDiscountPercent > 0 ? saleDiscountPercent * 0.01 : 0;
  return {
    saleEnabled,
    inSaleCollection,
    saleScope,
    saleDiscountPercent,
    discountFraction,
  };
}

/**
 * Derives whether the theme extra sale applies to subscription and/or one-time checkout.
 * Same truth table as `snippets/ia-buy-buttons.liquid` (`apply_sale_on_subscription` / `apply_sale_on_one_time`).
 *
 * @param {{ saleEnabled: boolean, inSaleCollection: boolean, saleScope: string }} config
 * @returns {{ applySub: boolean, applyOt: boolean }}
 */
function themeSaleFlags(config) {
  let applySub = false;
  let applyOt = false;
  if (config.saleEnabled && config.inSaleCollection) {
    switch (config.saleScope) {
      case "all":
        applySub = true;
        applyOt = true;
        break;
      case "one":
        applySub = false;
        applyOt = true;
        break;
      case "subscription":
        applySub = true;
        applyOt = false;
        break;
      default:
        break;
    }
  }
  return { applySub, applyOt };
}

/**
 * Rounds a money value in cents the same way Liquid `| round: 2` does on fractional cents.
 *
 * @param {number} n
 * @returns {number}
 */
function roundMoneyLikeLiquid(n) {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

/**
 * Applies the theme extra sale percent to an amount in cents.
 * Mirrors `ia-buy-buttons.liquid`: `discount_amount = amount | times: discount_percentage | round: 2`, then minus.
 *
 * @param {number} amountCents
 * @param {number} discountFraction - Multiplier (e.g. `0.1` for 10%).
 * @returns {number}
 */
function applyThemeExtraSaleToCents(amountCents, discountFraction) {
  if (!discountFraction || discountFraction <= 0 || !Number.isFinite(amountCents)) {
    return Number.isFinite(amountCents) ? amountCents : 0;
  }
  const discountAmount = roundMoneyLikeLiquid(amountCents * discountFraction);
  return roundMoneyLikeLiquid(amountCents - discountAmount);
}

class StayAISubscriptions extends HTMLElement {
  constructor() {
    super();
    this.productJSON = this.getAttribute("productData");
    this.product = JSON.parse(this.productJSON);
    this.initialVariant = this.getAttribute("currentVariantId");
    this.sellingPlanGroups = JSON.parse(this.getAttribute("sellingPlanGroups"));
    this.productFormId = this.getAttribute("productFormId");
    this.frequencyVisible = this.getAttribute("frequencyVisible");
  }
  connectedCallback() {
    this.initWidget();
    this.setupEventListeners();
  }
  initWidget() {
    this.purchaseTypeSubscription = this.querySelector("[for='purchaseTypeSubscription']");
    this.purchaseTypeOneTime = this.querySelector("[for='purchaseTypeOneTime']");
    this.sellingPlanSelector = this.querySelector(".stayai-subscription-dropdown");
    this.subscriptionBox = this.querySelector("[data-retextion-subscription-box]");
    this.subscriptionBoxContainer = this.querySelector(".stayai-subscription-frequency-container");
    this.radioOneTime = this.querySelector('input[value="purchaseTypeOneTime"]');
    this.radioSubscription = this.querySelector('input[value="purchaseTypeSubscription"]');
    const selectedInitialVariant = this.product.variants.find((variant) => variant.id === Number(this.initialVariant));
    this.currentVariant = selectedInitialVariant || this.product.variants[0];
    this._themeSaleConfig = parseThemeSaleConfigFromElement(this);
    this._themeSaleFlags = themeSaleFlags(this._themeSaleConfig);
    this.updateSellingPlans();
  }
  // Set up event listeners for the purchase type, frequency and variant selectors
  setupEventListeners() {
    const purchaseTypeRadioButtons = this.querySelectorAll('input[name="purchaseType"]');
    // Handle purchase type change
    purchaseTypeRadioButtons.forEach((radio) =>
      radio.addEventListener("change", () => {
        this[radio.value === "purchaseTypeSubscription" ? "showSubscriptionBox" : "hideSubscriptionBox"]();
        this.updateProductPrice(this.sellingPlanSelector.value);
      }),
    );
    // Handle frequency selector change
    this.sellingPlanSelector.addEventListener("change", (e) => {
      this.updateSellingPlanInput(e.target.value);
      this.updateProductPrice(e.target.value);
    });
    // Handle variant change from URL
    window.addEventListener("popstate", this.updateVariantID());
    this.observeUrlChanges();
    // Handle browser back/forward navigation
    window.addEventListener("pageshow", (event) => {
      if (event.persisted || performance.getEntriesByType("navigation")[0]?.type === "back_forward") {
        this.isSubscriptionOptionSelected()
          ? this.querySelector("label[for='purchaseTypeSubscription']").click()
          : this.querySelector("label[for='purchaseTypeOneTime']").click();
        this.updateSellingPlans();
      }
    });
  }
  // Get variant ID from the URL and update the variant
  updateVariantID = () => {
    const urlParams = new URLSearchParams(window.location.search);
    if (!urlParams.has("variant")) return;
    const variantId = Number(urlParams.get("variant"));
    if (variantId && variantId !== this.currentVariant.id) {
      this.currentVariant = this.product.variants.find((v) => v.id === variantId);
      this.updateSellingPlans();
    }
  };
  // Monitor URL changes without overriding history methods
  observeUrlChanges = () => {
    let lastUrl = window.location.href;
    new MutationObserver(() => {
      if (window.location.href !== lastUrl) {
        lastUrl = window.location.href;
        this.updateVariantID();
      }
    }).observe(document, { subtree: true, childList: true });
  };
  /**
   * First `selling_plan_allocation` for the current variant that matches Stay selling plan groups.
   *
   * @returns {object|null} Allocation object, or `null` if none match (same bundle filter as `updateSellingPlans`).
   */
  getFirstFilteredSellingPlanAllocation() {
    const groupMap = new Map(
      this.sellingPlanGroups.filter((g) => !g.options[0].name.toLowerCase().includes("bundle")).map((g) => [g.id, g]),
    );
    for (const spa of this.currentVariant.selling_plan_allocations || []) {
      const plan = groupMap.get(spa.selling_plan_group_id)?.selling_plans.find((sp) => sp.id === spa.selling_plan_id);
      if (plan) return spa;
    }
    return null;
  }

  // Populate the subscription frequency selector with selling plans for the current variant
  updateSellingPlans() {
    const stayWidget = this.querySelector(`.stayai-subscriptions`);
    if (!stayWidget) return;
    // Filter out bundle-type selling plan groups and map them by ID
    const groupMap = new Map(
      this.sellingPlanGroups.filter((g) => !g.options[0].name.toLowerCase().includes("bundle")).map((g) => [g.id, g]),
    );
    const sellingPlans = this.currentVariant.selling_plan_allocations
      .map((spa) => groupMap.get(spa.selling_plan_group_id)?.selling_plans.find((sp) => sp.id === spa.selling_plan_id))
      .filter(Boolean);
    if (sellingPlans.length === 0) {
      this.updateSellingPlanInput("");
      stayWidget.classList.toggle("is-visible", false);
      console.log("No SPs, hiding widget");
      return;
    } else stayWidget.classList.toggle("is-visible", true);
    // Clear existing options and populate dropdown
    this.sellingPlanSelector.innerHTML = sellingPlans
      .map((plan) => `<option value="${plan.id}">${plan.options[0].value}</option>`)
      .join("");
    if (!this.isSubscriptionOptionSelected()) {
      this.addSelectFrequencyOption();
      this.updateSellingPlanInput(this.getSellingPlanIdFromWidget());
    } else {
      this.updateSellingPlanInput(this.getSellingPlanIdFromWidget() || String(sellingPlans[0]?.id || ""));
    }
    // Show or hide the subscription frequency selector
    this.subscriptionBox.classList.toggle("is-visible", this.isSubscriptionOptionSelected());
    if (this.subscriptionBoxContainer)
      this.subscriptionBoxContainer.classList.toggle("subscriptionBoxContainer", this.isSubscriptionOptionSelected());
    // Update prices
    this.updateProductPrice(
      this.isSubscriptionOptionSelected() ? this.sellingPlanSelector.value : sellingPlans[0]?.id || "",
    );
  }
  // Add "Select frequency" option to the frequency selector
  addSelectFrequencyOption() {
    if (!this.sellingPlanSelector.querySelector('option[value=""]')) {
      this.sellingPlanSelector.insertAdjacentHTML("afterbegin", '<option value="" selected>Select frequency</option>');
    }
  }
  // Show the subscription frequency selector
  showSubscriptionBox() {
    this.subscriptionBox.classList.toggle("stayai-disable", false);
    this.subscriptionBox.classList.toggle("is-visible", true);
    if (this.subscriptionBoxContainer) this.subscriptionBoxContainer.classList.toggle("subscriptionBoxContainer", true);
    this.purchaseTypeSubscription.classList.toggle("is-selected", true);
    this.purchaseTypeOneTime.classList.toggle("is-selected", false);
    if (this.radioSubscription) this.radioSubscription.checked = true;
    if (this.radioOneTime) this.radioOneTime.checked = false;
    this.sellingPlanSelector.querySelector('option[value=""]')?.remove();
    this.updateSellingPlanInput(this.getSellingPlanIdFromWidget() || this.sellingPlanSelector.value);
  }
  // Hide the subscription frequency selector
  hideSubscriptionBox() {
    this.addSelectFrequencyOption();
    if (this.subscriptionBoxContainer) this.subscriptionBoxContainer.classList.toggle("is-visible", false);
    this.subscriptionBox.classList.toggle("is-visible", false);
    this.subscriptionBox.classList.toggle("disable", true);
    this.purchaseTypeSubscription.classList.toggle("is-selected", false);
    this.purchaseTypeOneTime.classList.toggle("is-selected", true);
    if (this.radioOneTime) this.radioOneTime.checked = true;
    if (this.radioSubscription) this.radioSubscription.checked = false;
    this.updateSellingPlanInput("");
  }
  // Check if the subscription option is selected (prefer native radio state)
  isSubscriptionOptionSelected() {
    if (this.radioSubscription && this.radioOneTime) {
      return this.radioSubscription.checked;
    }
    return this.purchaseTypeSubscription?.classList.contains("is-selected");
  }
  /** Selling plan ID from Stay.ai UI: empty when one-time is selected. */
  getSellingPlanIdFromWidget() {
    if (!this.isSubscriptionOptionSelected()) return "";
    const v = this.sellingPlanSelector?.value;
    return v === "" || v == null ? "" : String(v);
  }
  /** Ensure submitted selling_plan matches current Stay.ai selection before FormData is built. */
  syncSellingPlanForCartAdd() {
    this.updateSellingPlanInput(this.getSellingPlanIdFromWidget());
  }
  /** Cart/add forms that should receive the selling_plan hidden input (main PDP + sticky, etc.) */
  getProductForms() {
    const fallbackSelector =
      'form[action="/cart/add"][data-type="add-to-cart-form"]:not(.installment), ' +
      'form[action="/cart/add"][id*="product-form"]:not(.installment), ' +
      'form[action="/cart/add"][class*="shopify-product-form"]:not(.installment), ' +
      'form[action="/cart/add"][class*="product-form"]:not(.installment), ' +
      'form[action="/cart/add"]:not(.installment)';
    if (this.productFormId) {
      const explicit = document.querySelectorAll(this.productFormId);
      if (explicit.length > 0) return Array.from(explicit);
    }
    return Array.from(document.querySelectorAll(fallbackSelector));
  }
  /**
   * Writes hidden `selling_plan` on cart/add forms; disables ReCharge plan dropdowns so only Stay controls the value.
   * If multiple hidden `selling_plan` inputs exist (e.g. ReCharge Ci()), extras are removed so the cart receives one value.
   * ATC flows use `product-form.js` (sync before FormData + strip `selling_plan_*`).
   *
   * @param {string|undefined|null} sellingPlanId
   */
  updateSellingPlanInput(sellingPlanId) {
    const value = sellingPlanId === undefined || sellingPlanId === null ? "" : String(sellingPlanId);
    const productForms = this.getProductForms();
    if (productForms.length === 0) return;

    for (const productForm of productForms) {
      const rcPlanSelects = productForm.querySelectorAll(
        'select[name="selling_plan"], select[id^="selling_plan_"], select.rc-selling-plans__dropdown, select[data-plans-dropdown]',
      );
      const seen = new Set();
      rcPlanSelects.forEach((select) => {
        if (seen.has(select)) return;
        seen.add(select);
        select.setAttribute("data-stayai-neutralized-rc-plan", "true");
        select.removeAttribute("name");
        select.disabled = true;
      });

      let inputs = productForm.querySelectorAll("input[name='selling_plan']");
      if (inputs.length > 1) {
        const first = inputs[0];
        for (let i = 1; i < inputs.length; i++) {
          inputs[i].remove();
        }
        inputs = productForm.querySelectorAll("input[name='selling_plan']");
      }
      if (inputs.length === 0) {
        const hidden = document.createElement("input");
        hidden.type = "hidden";
        hidden.name = "selling_plan";
        hidden.value = value;
        productForm.appendChild(hidden);
      } else {
        inputs.forEach((el) => {
          el.value = value;
        });
      }
    }
    this.fireStayAiEvent("stay-ai-subscription-changed", value);
  }

  /**
   * Updates one-time and subscription price labels, `.subscription-savings`, and triggers ATC price sync.
   *
   * @param {string|number|undefined|null} sellingPlanId - Widget selling plan id, or empty/falsy when one-time is selected.
   *
   * @description
   * When the UI has no plan id (one-time selected), still resolves the first filtered allocation via
   * `getFirstFilteredSellingPlanAllocation()` so the subscription preview row and savings % stay populated.
   * How theme sale stacks on the subscription row and how `.subscription-savings` is computed are spelled out in
   * short comments next to that logic below (easier to follow while reading the implementation).
   */
  updateProductPrice(sellingPlanId) {
    const planIdInput =
      sellingPlanId !== undefined && sellingPlanId !== null && String(sellingPlanId).trim() !== ""
        ? String(sellingPlanId).trim()
        : "";
    let selectedSellingPlanAllocation = planIdInput
      ? this.currentVariant.selling_plan_allocations.find((spa) => String(spa.selling_plan_id) === planIdInput)
      : undefined;
    if (!selectedSellingPlanAllocation) {
      selectedSellingPlanAllocation = this.getFirstFilteredSellingPlanAllocation();
    }

    const oneTimeLblPrice = this.querySelector("[data-stayai-onetime-price]");
    const subscriptionLblPrice = this.querySelector("[data-stayai-subscription-price]");
    const savingLbl = this.querySelector(".subscription-savings");

    const cfg = this._themeSaleConfig || parseThemeSaleConfigFromElement(this);
    const flags = this._themeSaleFlags || themeSaleFlags(cfg);
    const frac = cfg.discountFraction || 0;

    const variantPriceCents = Number(this.currentVariant.price);
    const oneTimeCents =
      flags.applyOt && frac > 0 ? applyThemeExtraSaleToCents(variantPriceCents, frac) : variantPriceCents;
    const oneTimeDisplayFormatted = this.formatMoney(oneTimeCents);

    const compareAtPrice = this.formatMoney(this.currentVariant.compare_at_price);
    const hasValidCompareAt =
      Number(this.currentVariant.compare_at_price) > 0 &&
      Number(this.currentVariant.compare_at_price) !== variantPriceCents;

    const retailFormatted = this.formatMoney(variantPriceCents);
    const themeOtReduces = flags.applyOt && frac > 0 && oneTimeCents < variantPriceCents;

    let oneTimeInnerHtml;
    if (themeOtReduces) {
      oneTimeInnerHtml = `${srStrike(retailFormatted)}<span class="stayai-discounted-otp">${oneTimeDisplayFormatted}</span>`;
    } else if (hasValidCompareAt) {
      oneTimeInnerHtml = `${srStrike(compareAtPrice)}<span class="stayai-discounted-otp">${oneTimeDisplayFormatted}</span>`;
    } else {
      oneTimeInnerHtml = `<span class="stayai-discounted-otp">${oneTimeDisplayFormatted}</span>`;
    }

    if (oneTimeLblPrice && oneTimeLblPrice.innerHTML !== oneTimeInnerHtml) {
      oneTimeLblPrice.innerHTML = oneTimeInnerHtml;
    }

    // Update subscription price (theme extra % stacks on allocation price, same order as ia-buy-buttons)
    if (selectedSellingPlanAllocation) {
      let subCents = Number(selectedSellingPlanAllocation.price);
      if (flags.applySub && frac > 0) {
        subCents = applyThemeExtraSaleToCents(subCents, frac);
      }
      const subscriptionDisplayFormatted = this.formatMoney(subCents);
      const subscriptionInnerHtml =
        subscriptionDisplayFormatted !== retailFormatted
          ? `${srStrike(retailFormatted)}${subscriptionDisplayFormatted}`
          : subscriptionDisplayFormatted;

      if (subscriptionLblPrice && subscriptionLblPrice.innerHTML !== subscriptionInnerHtml) {
        subscriptionLblPrice.innerHTML = subscriptionInnerHtml;
      }

      // .subscription-savings: total % off variant retail for the *displayed* subscription price
      // (selling-plan allocation + theme extra sale when applySub). Matches ia-variants "Save X%" vs retail, not plan-only %.
      // Sticky bar mirrors this node via pdp-sticky-add-to-cart MutationObserver — no change there.
      if (savingLbl) {
        const retailCents = variantPriceCents;
        if (retailCents <= 0 || subCents >= retailCents) {
          savingLbl.textContent = "";
        } else {
          const effectivePcnt = Math.round((1 - subCents / retailCents) * 100);
          const label = effectivePcnt > 0 ? `${effectivePcnt}%` : "";
          if (savingLbl.textContent !== label) savingLbl.textContent = label;
        }
      }
    } else if (savingLbl) {
      savingLbl.textContent = "";
    }

    this._atcPriceSellingPlanId = sellingPlanId;
    this.syncAtcButtonPrices();
  }
  /**
   * Syncs add-to-cart button price markup (`.price-item--regular` / `.price-sale`) with the Stay widget output.
   *
   * @description
   * Updates the main PDP submit button matching
   * `button.product-form__submit[name='add']:not(.sticky-atc)` (scoped by `this.productFormId` when set),
   * and **one** of `#sticky-atc-subscribe` or `#sticky-atc-onetime` so the hidden sticky button is not overwritten.
   *
   * Liquid SSR in `snippets/pdp-sticky-add-to-cart.liquid` is first paint only; this method keeps cents aligned after load.
   */
  syncAtcButtonPrices() {
    const sellingPlanId = this._atcPriceSellingPlanId;
    const effectivePlanId = this.isSubscriptionOptionSelected()
      ? String(this.getSellingPlanIdFromWidget() || sellingPlanId || "").trim()
      : "";
    const selectedSellingPlanAllocation = effectivePlanId
      ? this.currentVariant.selling_plan_allocations.find((spa) => spa.selling_plan_id == effectivePlanId)
      : undefined;

    const cfg = this._themeSaleConfig || parseThemeSaleConfigFromElement(this);
    const flags = this._themeSaleFlags || themeSaleFlags(cfg);
    const frac = cfg.discountFraction || 0;

    const variantPriceCents = Number(this.currentVariant.price);
    const retailFormatted = this.formatMoney(variantPriceCents);

    const oneTimeCents =
      flags.applyOt && frac > 0 ? applyThemeExtraSaleToCents(variantPriceCents, frac) : variantPriceCents;
    const oneTimeDisplayFormatted = this.formatMoney(oneTimeCents);
    const themeOtReduces = flags.applyOt && frac > 0 && oneTimeCents < variantPriceCents;

    const compareAtPrice = this.formatMoney(this.currentVariant.compare_at_price);
    const hasValidCompareAt =
      Number(this.currentVariant.compare_at_price) > 0 &&
      Number(this.currentVariant.compare_at_price) !== variantPriceCents;

    const wantSub = this.isSubscriptionOptionSelected() && selectedSellingPlanAllocation;

    let subDisplayFormatted = retailFormatted;
    if (selectedSellingPlanAllocation) {
      let subCents = Number(selectedSellingPlanAllocation.price);
      if (flags.applySub && frac > 0) {
        subCents = applyThemeExtraSaleToCents(subCents, frac);
      }
      subDisplayFormatted = this.formatMoney(subCents);
    }

    const applyPriceToAtcButton = (btn) => {
      if (!btn) return;
      const regular = btn.querySelector(".price-item.price-item--regular");
      if (!regular) return;
      const sale = btn.querySelector(".price-sale");

      if (wantSub) {
        const useSplit = subDisplayFormatted !== retailFormatted;
        if (useSplit && sale) {
          const nextReg = srStrike(retailFormatted);
          if (regular.innerHTML !== nextReg || sale.textContent !== subDisplayFormatted || sale.style.display === "none") {
            regular.innerHTML = nextReg;
            sale.textContent = subDisplayFormatted;
            sale.style.display = "";
            if (flags.applySub && frac > 0) {
              sale.setAttribute("data-discount", String(frac));
            } else {
              sale.removeAttribute("data-discount");
            }
          }
        } else if (sale) {
          if (regular.innerHTML !== subDisplayFormatted || sale.style.display !== "none") {
            regular.innerHTML = subDisplayFormatted;
            sale.textContent = "";
            sale.style.display = "none";
          }
        } else {
          regular.innerHTML =
            subDisplayFormatted !== retailFormatted
              ? `${srStrike(retailFormatted)}${subDisplayFormatted}`
              : subDisplayFormatted;
        }
        return;
      }

      if (themeOtReduces && sale) {
        const nextReg = srStrike(retailFormatted);
        if (regular.innerHTML !== nextReg || sale.textContent !== oneTimeDisplayFormatted || sale.style.display === "none") {
          regular.innerHTML = nextReg;
          sale.textContent = oneTimeDisplayFormatted;
          sale.style.display = "";
          if (frac > 0) sale.setAttribute("data-discount", String(frac));
        }
        return;
      }

      const oneTimeHtml = hasValidCompareAt ? `${srStrike(compareAtPrice)} ${oneTimeDisplayFormatted}` : oneTimeDisplayFormatted;
      if (hasValidCompareAt && sale) {
        const nextReg = srStrike(compareAtPrice);
        if (regular.innerHTML !== nextReg || sale.textContent !== oneTimeDisplayFormatted || sale.style.display === "none") {
          regular.innerHTML = nextReg;
          sale.textContent = oneTimeDisplayFormatted;
          sale.style.display = "";
          sale.removeAttribute("data-discount");
        }
        return;
      }

      if (regular.innerHTML !== oneTimeHtml) {
        regular.innerHTML = oneTimeHtml;
      }
      if (sale) {
        sale.textContent = "";
        sale.style.display = "none";
      }
    };

    const mainSelector = this.productFormId
      ? `${this.productFormId} button.product-form__submit[name='add']:not(.sticky-atc)`
      : ".product-form__buttons button.product-form__submit[name='add']:not(.sticky-atc)";
    document.querySelectorAll(mainSelector).forEach(applyPriceToAtcButton);

    if (wantSub) {
      applyPriceToAtcButton(document.getElementById("sticky-atc-subscribe"));
    } else {
      applyPriceToAtcButton(document.getElementById("sticky-atc-onetime"));
    }
  }
  // Toggle checked state based on the currently checked radio button
  toggleRadioButtons() {
    this.radioOneTime.checked = !this.radioOneTime.checked;
    this.radioSubscription.checked = !this.radioSubscription.checked;
  }
  // Format money based on Shopify's active currency
  formatMoney(money) {
    const { Shopify, theme } = window;
    const currency = Shopify?.currency?.active || "USD";
    const locale = Shopify?.locale || "en";
    const cents = Number(money);
    const amount = cents / 100;
    const hasFractionalCents = cents % 100 !== 0;
    const hasShopifyFormatMoney = Shopify && typeof Shopify?.formatMoney === "function";
    const hasThemeUtilsFormatMoney = theme?.utils && typeof theme?.utils?.formatMoney === "function";
    const formattedMoney = amount.toFixed(2);
    const currencyFormatter = new Intl.NumberFormat(locale, {
      currency,
      style: "currency",
      minimumFractionDigits: hasFractionalCents ? 2 : 0,
      maximumFractionDigits: hasFractionalCents ? 2 : 0,
    });
    if (!hasShopifyFormatMoney && !hasThemeUtilsFormatMoney) {
      return currencyFormatter.format(Number(formattedMoney));
    }
    if (!hasFractionalCents) {
      return currencyFormatter.format(amount);
    }
    const formatted = hasShopifyFormatMoney ? Shopify.formatMoney(money) : theme.utils.formatMoney(money);
    return formatted || currencyFormatter.format(Number(formattedMoney));
  }
  fireStayAiEvent(eventName, sellingPlanId) {
    const event = new CustomEvent(eventName, {
      detail: {
        sellingPlanId: sellingPlanId,
      },
    });
    window.dispatchEvent(event);
  }
}

export { parseThemeSaleConfigFromElement, themeSaleFlags, applyThemeExtraSaleToCents, roundMoneyLikeLiquid };

// Define the custom element
customElements.define("purchase-options", StayAISubscriptions);
